/**
 * Shift Type Configuration
 * Defines the different types of shifts employees can clock into
 * Shift types are independent of job roles - a server can clock into wine_training
 */

import {
  UtensilsCrossed,
  Wine,
  PartyPopper,
  GraduationCap,
  Package,
  type LucideIcon,
} from 'lucide-react';

export type ShiftType = 'server' | 'sommelier' | 'event' | 'wine_training' | 'inventory';

export interface ShiftTypeConfig {
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  requiresPreShift: boolean;
  destination: string;
  description: string;
}

export const SHIFT_TYPES: Record<ShiftType, ShiftTypeConfig> = {
  server: {
    label: 'Server',
    icon: UtensilsCrossed,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    requiresPreShift: true,
    destination: '/pre-shift',
    description: 'Regular service shift',
  },
  sommelier: {
    label: 'Sommelier',
    icon: Wine,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    requiresPreShift: true,
    destination: '/pre-shift',
    description: 'Wine service focus',
  },
  event: {
    label: 'Event',
    icon: PartyPopper,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    requiresPreShift: true,
    destination: '/pre-shift',
    description: 'Special event work',
  },
  wine_training: {
    label: 'Wine Training',
    icon: GraduationCap,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    requiresPreShift: false,
    destination: '/tables',
    description: 'Training session',
  },
  inventory: {
    label: 'Inventory',
    icon: Package,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    requiresPreShift: false,
    destination: '/tables',
    description: 'Inventory counting & prep',
  },
};

/**
 * Get the shift type configuration
 * Falls back to 'server' if not found
 */
export const getShiftTypeConfig = (shiftType: string): ShiftTypeConfig => {
  return SHIFT_TYPES[shiftType as ShiftType] || SHIFT_TYPES.server;
};

/**
 * Get all available shift types as an array
 */
export const getAvailableShiftTypes = (): Array<{ key: ShiftType; config: ShiftTypeConfig }> => {
  return (Object.keys(SHIFT_TYPES) as ShiftType[]).map((key) => ({
    key,
    config: SHIFT_TYPES[key],
  }));
};

/**
 * Check if a shift type requires pre-shift checklist
 */
export const shiftTypeRequiresPreShift = (shiftType: string): boolean => {
  return getShiftTypeConfig(shiftType).requiresPreShift;
};
